import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isValidSlug, type BoothManifest } from '@photobooth/shared';
import { fetchBoothManifest } from '../api/client';
import { Button } from '../ui/Button';
import { BoothPage } from './BoothPage';
import { logger } from '../utils/log';

const log = logger('custom-booth');

/**
 * Route handler for `/{slug}`. Validates the slug, fetches the manifest, then
 * renders the same BoothPage with the manifest passed through as a prop.
 */
export function CustomBoothPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<BoothManifest | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'not-found' | 'expired'>(
    'loading',
  );

  useEffect(() => {
    let cancelled = false;
    if (!isValidSlug(slug)) {
      setState('not-found');
      return;
    }
    (async () => {
      log.info('loading manifest', slug);
      const m = await fetchBoothManifest(slug);
      if (cancelled) return;
      if (!m) {
        setState('not-found');
        return;
      }
      if (new Date(m.expiresAt).getTime() < Date.now()) {
        setState('expired');
        return;
      }
      setManifest(m);
      setState('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === 'loading') {
    return (
      <div style={fullScreenCenter}>
        <p style={{ color: 'var(--text-dim)' }}>Loading booth…</p>
      </div>
    );
  }
  if (state === 'not-found') {
    return (
      <div style={fullScreenCenter}>
        <h1>Booth not found</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 360, textAlign: 'center' }}>
          The booth at <code>/{slug}</code> doesn&apos;t exist (yet?). Want to make one?
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={() => navigate('/builder')}>
            Build a booth
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')}>
            Use the default
          </Button>
        </div>
      </div>
    );
  }
  if (state === 'expired') {
    return (
      <div style={fullScreenCenter}>
        <h1>This booth has expired</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 360, textAlign: 'center' }}>
          Custom booths last 30 days. The owner can build a new one any time.
        </p>
        <Button variant="primary" onClick={() => navigate('/builder')}>
          Build your own
        </Button>
      </div>
    );
  }

  return <BoothPage manifest={manifest} />;
}

const fullScreenCenter: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  color: 'var(--text)',
};
